import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { sendHandoffEmail } from "../lib/api";
import { readFirstTouch } from "../lib/attribution";
import { pushGtmEvent } from "../lib/gtm-events";
import { chromeIntentUrl, inAppBrowserAppName, type InAppBrowser } from "../lib/in-app-browser";
import { urlWithPrefillEmail } from "../lib/prefill-email-store";
import { Turnstile } from "./Turnstile";

export interface HandoffTurnstile {
  /** Whether this host enforces Turnstile (or the E2E test widget is on). */
  show: boolean;
  /** False until /api/bootstrap settles, so the widget renders with the real key. */
  ready: boolean;
  siteKey: string | null;
  /** `?turnstile=test` off production: the server must verify with the test secret. */
  testMode: boolean;
}

interface InAppBrowserHandoffProps {
  browser: InAppBrowser;
  /** From the landing page handoff. Put back into the copied link so the
   *  other browser still prefills Google, and implies the LP emailed it. */
  prefillEmail: string | null;
  turnstile: HandoffTurnstile;
}

type CopyState = "idle" | "copied" | "manual";
type SendState = { status: "idle" | "sending" | "sent" } | { status: "error"; message: string };

const secondaryButton =
  "inline-flex h-11 w-full items-center justify-center gap-2 rounded-full border border-neutral-950/10 bg-white px-5 text-[15px] font-semibold text-ink transition-colors hover:bg-neutral-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500";

/** utm_* and fbclid from this URL, topped up from the stored first touch.
 *  The server drops anything else. */
function attributionParams(): Record<string, string> {
  const params: Record<string, string> = {};
  if (typeof window === "undefined") return params;
  for (const [key, value] of new URLSearchParams(window.location.search)) {
    const lower = key.toLowerCase();
    if ((lower.startsWith("utm_") || lower === "fbclid") && value) params[key] = value;
  }
  const firstTouch = readFirstTouch();
  if (firstTouch) {
    for (const [key, value] of Object.entries(firstTouch)) {
      if ((key.startsWith("utm_") || key === "fbclid") && typeof value === "string" && value) {
        params[key] ??= value;
      }
    }
  }
  return params;
}

function MenuIllustration() {
  return (
    <svg
      viewBox="0 0 280 150"
      aria-hidden="true"
      focusable="false"
      className="mx-auto block h-auto w-full max-w-[280px]"
    >
      <rect
        x="0.5"
        y="0.5"
        width="279"
        height="149"
        rx="16"
        className="fill-white stroke-purple-200"
      />
      <path d="M0.5 44.5h279" className="stroke-purple-100" />
      <path
        d="M20 16l12 12M32 16L20 28"
        strokeWidth="2.5"
        strokeLinecap="round"
        className="stroke-neutral-400"
      />
      <rect x="62" y="14" width="140" height="16" rx="8" className="fill-neutral-100" />
      <circle
        cx="248"
        cy="22"
        r="17"
        className="fill-purple-50 stroke-purple-500"
        strokeWidth="2"
      />
      <circle cx="240" cy="22" r="2.6" className="fill-purple-700" />
      <circle cx="248" cy="22" r="2.6" className="fill-purple-700" />
      <circle cx="256" cy="22" r="2.6" className="fill-purple-700" />
      <rect
        x="112"
        y="54"
        width="156"
        height="86"
        rx="12"
        className="fill-white stroke-neutral-200"
      />
      <rect x="128" y="68" width="96" height="8" rx="4" className="fill-neutral-200" />
      <rect
        x="120"
        y="85"
        width="140"
        height="26"
        rx="8"
        className="fill-purple-50 stroke-purple-500"
        strokeWidth="1.5"
      />
      <text x="132" y="102.5" className="fill-purple-700 font-sans text-[12px] font-bold">
        Open in browser
      </text>
      <rect x="128" y="122" width="72" height="8" rx="4" className="fill-neutral-200" />
    </svg>
  );
}

function StepNumber({ n }: { n: number }) {
  return (
    <span
      aria-hidden="true"
      className="flex size-6 shrink-0 items-center justify-center rounded-full bg-purple-500 text-xs font-bold text-white"
    >
      {n}
    </span>
  );
}

export function InAppBrowserHandoff({
  browser,
  prefillEmail,
  turnstile,
}: InAppBrowserHandoffProps) {
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const [sendState, setSendState] = useState<SendState>({ status: "idle" });
  const [emailInput, setEmailInput] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // Bumped after a failed send: Turnstile tokens are single use, so the
  // widget has to mint a fresh one before the next attempt.
  const [turnstileKey, setTurnstileKey] = useState(0);
  const clearTurnstileToken = useCallback(() => setTurnstileToken(null), []);
  const linkRef = useRef<HTMLInputElement>(null);
  const href =
    typeof window === "undefined" ? "" : urlWithPrefillEmail(window.location.href, prefillEmail);
  const appName = inAppBrowserAppName(browser.app);
  const platform = browser.ios ? "ios" : browser.android ? "android" : "other";
  const target = browser.android ? "Chrome" : browser.ios ? "Safari" : "Safari or Chrome";
  const menu = browser.android
    ? { glyph: "⋮", label: "three vertical dots" }
    : { glyph: "•••", label: "three dots" };
  const needsToken = turnstile.show && !turnstileToken;
  const sending = sendState.status === "sending";

  useEffect(() => {
    if (copyState !== "copied") return;
    const t = window.setTimeout(() => setCopyState("idle"), 2400);
    return () => window.clearTimeout(t);
  }, [copyState]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(href);
      setCopyState("copied");
    } catch {
      // Some webviews ship without clipboard permission. Reveal the link
      // pre-selected so a long-press copy still works.
      setCopyState("manual");
      window.requestAnimationFrame(() => {
        linkRef.current?.focus();
        linkRef.current?.select();
      });
    }
  }

  async function sendLink(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = prefillEmail ?? emailInput.trim();
    if (!email || sending || needsToken) return;
    setSendState({ status: "sending" });
    try {
      await sendHandoffEmail(
        { email, turnstileToken: turnstileToken ?? undefined, params: attributionParams() },
        turnstile.testMode,
      );
      setSendState({ status: "sent" });
      pushGtmEvent("handoff_link_emailed", {
        app: browser.app,
        platform,
        had_prefill: prefillEmail !== null,
      });
    } catch (err) {
      setSendState({
        status: "error",
        message: err instanceof Error ? err.message : "Sending failed. Try again.",
      });
      setTurnstileToken(null);
      setTurnstileKey((k) => k + 1);
    }
  }

  return (
    <section
      aria-labelledby="in-app-handoff-title"
      className="rounded-2xl border border-purple-200 bg-purple-50/60 p-5"
    >
      <div className="flex items-start gap-3">
        <i
          className="fa-regular fa-arrow-up-right-from-square mt-1 shrink-0 text-base text-purple-600"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <h2
            id="in-app-handoff-title"
            className="font-display text-lg font-bold leading-snug tracking-[-0.01em] text-ink text-balance"
          >
            Open in {target} to connect GA4
          </h2>
          <p className="mt-1.5 text-sm leading-[1.5] text-neutral-700 text-pretty">
            Google does not allow GA4 connections inside the {appName} app. Open this page in your
            phone's browser to finish. It takes about a minute.
          </p>
        </div>
      </div>

      {browser.ios ? (
        <div className="mt-5">
          <MenuIllustration />
          <ol className="mt-4 space-y-3 text-sm leading-[1.45] text-ink">
            <li className="flex items-start gap-3">
              <StepNumber n={1} />
              <span className="pt-0.5">
                Tap <span aria-hidden="true">•••</span>
                <span className="sr-only">the three dots menu</span> in the top right corner.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <StepNumber n={2} />
              <span className="pt-0.5">
                Choose <strong className="font-semibold">Open in browser</strong>.
                <span className="mt-0.5 block text-[13px] text-neutral-600">
                  Some apps say Open in external browser or Open in Safari.
                </span>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <StepNumber n={3} />
              <span className="pt-0.5">Sign in with Google there to connect GA4.</span>
            </li>
          </ol>
        </div>
      ) : null}

      <div className="mt-5 flex flex-col gap-3">
        {browser.android ? (
          <a href={chromeIntentUrl(href)} className="ts-cta w-full justify-center">
            Open in Chrome <span className="ts-cta-arrow">→</span>
          </a>
        ) : null}
        <button type="button" onClick={() => void copyLink()} className={secondaryButton}>
          <i
            className={copyState === "copied" ? "fa-solid fa-check" : "fa-regular fa-copy"}
            aria-hidden="true"
          />
          {copyState === "copied" ? "Link copied" : "Copy link"}
        </button>
        <p role="status" className="sr-only">
          {copyState === "copied"
            ? "Link copied"
            : copyState === "manual"
              ? "Copy did not work here. The link is selected below."
              : ""}
        </p>
        {copyState === "manual" ? (
          <label className="block text-xs text-neutral-600">
            Press and hold to copy this link
            <input
              ref={linkRef}
              readOnly
              value={href}
              onFocus={(e) => e.currentTarget.select()}
              className="mt-1.5 block w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 font-mono text-xs text-ink"
            />
          </label>
        ) : null}
      </div>

      {browser.ios ? null : (
        <p className="mt-5 text-sm leading-[1.45] text-neutral-700">
          Or tap the <span aria-hidden="true">{menu.glyph}</span>
          <span className="sr-only">{menu.label}</span> menu and choose to open this page in{" "}
          {browser.android ? "Chrome" : "your browser"}.
        </p>
      )}

      <form
        onSubmit={(e) => void sendLink(e)}
        aria-labelledby="in-app-handoff-email-title"
        className="mt-5 border-t border-purple-200/70 pt-5"
      >
        <h3
          id="in-app-handoff-email-title"
          className="font-display text-[15px] font-bold leading-snug text-ink"
        >
          Email me the link
        </h3>
        {prefillEmail ? (
          <p className="mt-1 text-sm leading-[1.45] text-neutral-700 text-pretty">
            We also sent this link to{" "}
            <span className="font-semibold [overflow-wrap:anywhere]">{prefillEmail}</span> when you
            asked for your report.
          </p>
        ) : (
          <p className="mt-1 text-sm leading-[1.45] text-neutral-700 text-pretty">
            Open it from your inbox in your phone's browser, or on your laptop.
          </p>
        )}

        {sendState.status === "sent" ? (
          <p
            role="status"
            className="mt-3 flex items-start gap-2 rounded-lg bg-white px-3 py-2.5 text-sm font-medium text-ink"
          >
            <i className="fa-solid fa-check mt-[3px] shrink-0 text-purple-600" aria-hidden="true" />
            Sent. Check your inbox on this phone.
          </p>
        ) : (
          <div className="mt-3 flex flex-col gap-3">
            {prefillEmail ? null : (
              <label className="block text-sm font-medium text-ink">
                Your email
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="you@company.com"
                  className="mt-1.5 block h-11 w-full rounded-lg border border-neutral-300 bg-white px-3 text-[15px] text-ink placeholder:text-neutral-400 focus:border-purple-300 focus:outline-none focus:ring-3 focus:ring-purple-100"
                />
              </label>
            )}
            {turnstile.show ? (
              turnstile.ready ? (
                <Turnstile
                  key={turnstileKey}
                  onToken={setTurnstileToken}
                  onError={clearTurnstileToken}
                  onExpired={clearTurnstileToken}
                  siteKey={turnstile.siteKey}
                />
              ) : (
                <div
                  role="status"
                  aria-busy="true"
                  aria-label="Loading security challenge"
                  className="h-16 animate-pulse rounded-md bg-white"
                />
              )
            ) : null}
            <button
              type="submit"
              disabled={sending || needsToken}
              className={
                browser.android
                  ? `${secondaryButton} disabled:cursor-not-allowed disabled:opacity-60`
                  : "ts-cta h-auto! min-h-11 w-full justify-center whitespace-normal! py-2.5 text-center [overflow-wrap:anywhere] disabled:cursor-not-allowed disabled:opacity-60"
              }
            >
              <i className="fa-regular fa-envelope" aria-hidden="true" />
              {sending
                ? "Sending…"
                : prefillEmail
                  ? `Email the link to ${prefillEmail}`
                  : "Email me the link"}
            </button>
            {sendState.status === "error" ? (
              <p
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {sendState.message}
              </p>
            ) : null}
          </div>
        )}
      </form>
    </section>
  );
}
